/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */

"use client";

import { logger } from "@/lib/utils/logger";
import { useEffect, useState } from "react";
import { useUIPreferences } from "../preferences/use-user-preferences";

export type TableDensity = "comfortable" | "compact";

export interface TablePrefs {
  hiddenColumns?: string[];
  density?: TableDensity;
  pageSize?: number;
  sort?: { column?: string | null; direction?: "asc" | "desc" };
  views?: Array<{
    id: string;
    name: string;
    value: {
      searchTerm?: string;
      hiddenColumns?: string[];
      density?: TableDensity;
      pageSize?: number;
      sort?: { column?: string | null; direction?: "asc" | "desc" };
    };
  }>;
  selectedViewId?: string;
}

export function useTablePreferences(tableId?: string) {
  const { ui, updateUI } = useUIPreferences();
  const [serverPrefs, setServerPrefs] = useState<TablePrefs | null>(null);
  const isReady = Boolean(tableId);

  useEffect(() => {
    if (!tableId) return;
    const tables = ui?.tables as Record<string, TablePrefs> | undefined;
    if (tables && tables[tableId]) {
      const tablePrefs = tables[tableId];
      if (tablePrefs) {
        setServerPrefs(tablePrefs);
      }
    }
  }, [ui, tableId]);

  const savePrefs = async (partial: TablePrefs) => {
    if (!tableId) return;
    try {
      const current =
        (ui?.tables as Record<string, TablePrefs> | undefined) ?? {};
      const nextForTable: TablePrefs = {
        ...(current[tableId] ?? {}),
        ...partial,
      };
      const next = { ...current, [tableId]: nextForTable };
      await updateUI({ ...ui, tables: next });
      setServerPrefs(nextForTable);
    } catch (error) {
      logger.error("Failed to save table preferences:", error);
    }
  };

  return { serverPrefs, savePrefs, isReady };
}
